import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { useSession } from '@/auth/SessionProvider'
import { useApi } from '@/lib/ConfigProvider'
import { formatMoney } from '@/lib/money'
import { CURRENCY_NAME, CURRENCY_SYMBOL } from '@/lib/currencyNames'
import { SendPanel } from '@/components/SendPanel'
import { fetchAccounts } from '@/accounts/accounts'
import type { Account, AccountsState } from '@/accounts/accounts'
import { fetchBalance, isStale } from '@/accounts/balances'
import type { BalanceState } from '@/accounts/balances'
import { fetchTransactions } from '@/activity/transactions'
import type { Transaction, TransactionsState } from '@/activity/transactions'
import { FIXTURE_CARD } from '@/data/fixtures'
import { activityMessage, holdingsMessage, shortfallNote, totalHeld } from './overviewCopy'
import './Overview.css'

/** "Wednesday, 29 July" — the design's eyebrow. */
function formatDay(d: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d)
}

/** "Evening, Mia" — greeting by wall-clock hour, first name only. */
function greeting(d: Date, firstName: string | null): string {
  const h = d.getHours()
  const part = h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening'
  // No name rather than a fixture's name (#83). Greeting nobody is a little
  // colder; greeting somebody by a stranger's name is a lie about who they are.
  return firstName === null ? part : `${part}, ${firstName}`
}

/** "Today", "28 Jul" — the activity table's date column. */
function formatActivityDate(iso: string, now: Date): string {
  const d = new Date(iso)
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) return 'Today'
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(d)
}

export function Overview() {
  // `now` is read once per render rather than per row, so every date on the
  // screen is relative to the same instant. A table that renders "Today" in one
  // row and "29 Jul" in the next because midnight passed mid-map is a bug that
  // only ever reproduces at 00:00.
  const now = new Date()
  const { state: session } = useSession()

  // GREET THE PERSON WHO IS ACTUALLY SIGNED IN, OR NOBODY (#83).
  //
  // This read FIXTURE_CUSTOMER's first name, so a signed-in customer was
  // addressed as "Mia" with their own email in the rail two inches away. Of
  // everything invented on this screen that was the most personal, and it is
  // the one part the session can make true.
  const firstName =
    session.status === 'authenticated' ? (session.identity.label.split(/[\s@]/)[0] ?? null) : null

  const api = useApi()

  // Overlapping reads guarded the way Balances.tsx guards them (#57). Nothing
  // on this screen re-reads today, so this is defence rather than a fix — but
  // the property being relied on is a rendering detail, and anyone who later
  // adds a refresh control reintroduces the failure silently.
  const generation = useRef(0)

  const [accounts, setAccounts] = useState<AccountsState>({ status: 'loading' })
  /**
   * One entry per account reference, filled in as each balance lands.
   *
   * A map rather than a list, and each account's balance kept SEPARATE, so a
   * `503` on one does not blank the others (#140). The route answers per
   * account and the failures are per account; collapsing them into one state
   * would mean a single stale snapshot hid every other figure the customer
   * holds.
   */
  const [balances, setBalances] = useState<Readonly<Record<string, BalanceState>>>({})
  const [activity, setActivity] = useState<TransactionsState>({ status: 'loading' })

  const load = useCallback(async () => {
    const mine = ++generation.current

    // Two independent reads. The history does not wait on the accounts, and
    // neither failing hides the other.
    void fetchTransactions(api, { limit: 5 }).then((t) => {
      if (mine === generation.current) setActivity(t)
    })

    const next = await fetchAccounts(api)
    if (mine !== generation.current) return
    setAccounts(next)
    if (next.status !== 'ready') return

    setBalances(
      Object.fromEntries(next.accounts.map((a) => [a.accountRef, { status: 'loading' } as BalanceState])),
    )
    for (const account of next.accounts) {
      void fetchBalance(api, account.accountRef).then((b) => {
        if (mine !== generation.current) return
        setBalances((prev) => ({ ...prev, [account.accountRef]: b }))
      })
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const held = totalHeld(accounts, balances)

  return (
    <>
      <header className="ov__head">
        <div className="ov__greeting">
          <span className="eyebrow">{formatDay(now)}</span>
          <h1 className="ov__hello">{greeting(now, firstName)}</h1>
        </div>

        <div className="ov__actions">
          <div className="ov__search">
            <Icon name="Search" size={18} />
            <span>Search people, payouts, transactions</span>
          </div>
          <button type="button" className="ov__iconBtn" aria-label="Notifications">
            <Icon name="Bell" size={18} />
          </button>
          {/*
            A link, not a button, now that there is somewhere to go (#90). It
            was a dead control for as long as Payments was a "not built yet"
            page — and a control that looks live and is not is the thing this
            repo keeps filing issues about.
          */}
          <Link to="/payments" className="ov__send">
            <Icon name="Transfer" size={18} />
            <span>Send money</span>
          </Link>
        </div>
      </header>

      <section className="ov__summary" aria-label="Summary">
        <article className="tile">
          {/*
            "Ready to spend", not "Everything you hold" (#170).

            The figure below is `available` now, and `available` is net of
            anything already committed to a payment — so "everything you hold"
            would be a false label on it whenever a cash-out is outstanding.
            The label had to move with the figure; leaving it would have traded
            one wrong sentence for another.
          */}
          <span className="eyebrow">Ready to spend</span>
          {held.kind === 'total' ? (
            <span className="tile__figure tnum">
              {formatMoney(held.total, { withCurrency: true })}
            </span>
          ) : (
            <span className="tile__figure tile__figure--quiet">
              {held.kind === 'loading'
                ? '—'
                : held.kind === 'mixed'
                  ? held.currencies.join(' · ')
                  : held.kind === 'partial'
                    ? 'Not all of it'
                    : held.kind === 'unknown'
                      ? 'We could not look'
                      : 'Nothing yet'}
            </span>
          )}
          <span className="tile__note">
            {held.kind === 'total'
              ? shortfallNote(held.shortfall)
              : held.kind === 'loading'
                ? 'Reading your accounts…'
                : held.kind === 'mixed'
                  ? // Not a hedge and not a missing feature: adding these needs
                    // a valuation rate nobody publishes to this app, and a
                    // number we made up would be one nothing can reconcile.
                    'Adding these together needs a rate we are not given, so each is shown on its own below.'
                  : held.kind === 'partial'
                    ? 'One of your accounts could not report just now, so this would be short. The ones that did are below.'
                    : held.kind === 'unknown'
                      ? // NOT "you hold nothing" (#22). That sends somebody to
                        // support about their onboarding when the truth is that
                        // the read failed.
                        'We could not read your accounts, so this is not a figure of nil.'
                      : 'When you hold something, it will be here.'}
          </span>
        </article>

        {/*
          NO FIGURE, because there is no route (#140, go-wallet#974).

          This said "9,420.00 · Across 4 platforms · 18% up on June" and every
          part of it was invented — a number, a count and a trend, none of them
          from anywhere. Saying where the figure will come from is a smaller
          claim than a fabricated one and the only true thing available.
        */}
        <article className="tile">
          <span className="eyebrow">Earned in July</span>
          <span className="tile__figure tile__figure--quiet">Not yet</span>
          {/*
            NO ISSUE NUMBER ON A CUSTOMER SCREEN. It read "It is
            go-wallet#974" in a code font, which is a note from us to us: it
            tells the person reading it nothing they can act on, and it says
            plainly that they are looking at an unfinished internal tool.

            What is still true and worth saying is that the figure is not
            available — that stays, because #22 and #140 are both about not
            letting "we cannot tell you" render as a number or as a nil.
          */}
          <span className="tile__note">We cannot work this out yet.</span>
        </article>

        <article className="tile">
          {/*
            Invariant 1: we never hold customer funds. This figure is what
            upstream platforms have not released yet — it is not a balance in
            our ledger and the copy must never imply that it is.

            The accent styling and the "Get it today for 1.9%" button are both
            gone with the figure. That button is an ADVANCE against money not
            yet released — a credit product, not a missing endpoint — and
            offering one against a number we do not have is the worst version of
            this screen's whole problem.
          */}
          <span className="eyebrow">Waiting on platforms</span>
          <span className="tile__figure tile__figure--quiet">Not yet</span>
          <span className="tile__note">
            What platforms have not released to you is not something we can read yet.
          </span>
        </article>
      </section>

      <div className="ov__body">
        <div className="ov__main">
          <section aria-labelledby="holdings-heading">
            <header className="ov__sectionHead">
              <h2 id="holdings-heading" className="ov__sectionTitle">
                Holdings
              </h2>
              <button type="button" className="ov__sectionAction">
                Add currency
              </button>
            </header>

            {accounts.status === 'ready' ? (
              <div className="holdings__scroll">
              <table className="holdings" aria-labelledby="holdings-heading">
                <thead>
                  <tr>
                    <th scope="col" className="eyebrow" colSpan={2}>
                      Account
                    </th>
                    <th scope="col" className="eyebrow holdings__right">
                      Held · available
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.accounts.map((a) => (
                    <HoldingRow
                      key={a.accountRef}
                      account={a}
                      balance={balances[a.accountRef] ?? { status: 'loading' }}
                      now={now}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <p className="ov__empty">{holdingsMessage(accounts)}</p>
            )}
          </section>

          <section aria-labelledby="activity-heading">
            <header className="ov__sectionHead">
              <h2 id="activity-heading" className="ov__sectionTitle">
                Recent activity
              </h2>
              <button type="button" className="ov__sectionAction">
                Statements
              </button>
            </header>

            {/*
              `holdings__name`, `__note` and `__amount` go on SPANS INSIDE a
              cell, never on the cell (#135).

              All three set `display: block`, which is what stacks the
              counterparty over its description inside one column — and applied
              to a `<td>` it takes that cell out of the table layout entirely.
              Every field became its own full-width block, one under another,
              each wearing the `box-shadow: inset 0 -1px 0` that draws the row
              rule, so the row read as four stacked lines separated by four
              short rules.

              #135 guessed a selector had been left behind by #121's rename.
              It had not: the rule matched exactly as written. The classes are
              INNER ones — they describe a span's role within a cell — and
              reusing them as column classes gave a `<td>` a declaration that
              only ever made sense on a span. The rename made them look
              general; they never were.

              The structure below mirrors the Holdings table above, which is
              why that one always rendered correctly on the same stylesheet.
            */}
            {activity.status === 'ready' ? (
              <div className="holdings__scroll">
              <table className="holdings" aria-labelledby="activity-heading">
                <thead className="visually-hidden">
                  <tr>
                    {/* Two columns: the disc and the counterparty/description
                        pair beside it (#135). */}
                    <th scope="col" colSpan={2}>
                      Counterparty and description
                    </th>
                    <th scope="col">Date</th>
                    <th scope="col">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.transactions.map((t) => (
                    <ActivityRow key={t.ref} tx={t} now={now} />
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <p className="ov__empty">{activityMessage(activity)}</p>
            )}
          </section>
        </div>

        <aside className="ov__side" aria-label="Send money and card">
          <SendPanel />

          <section className="card" aria-label="Card">
            <header className="card__head">
              <span className="card__mark">TESORA</span>
              <span className="card__contactless">
                <Icon name="Contactless" size={20} />
              </span>
            </header>

            <div className="card__limit">
              <span className="card__chip">
                <Icon name="Chip" size={24} />
              </span>
              <span
                className="card__track"
                role="progressbar"
                aria-label="Period limit used"
                aria-valuenow={Math.round(FIXTURE_CARD.limitUsed * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span
                  className="card__fill"
                  style={{ width: `${FIXTURE_CARD.limitUsed * 100}%` }}
                />
              </span>
            </div>

            <footer className="card__foot">
              <div className="card__id">
                <span className="card__tail tnum">·· ·· ·· {FIXTURE_CARD.maskedTail}</span>
                <span className="card__spend tnum">
                  {FIXTURE_CARD.label} · {formatMoney(FIXTURE_CARD.spentThisPeriod)} spent in July
                </span>
              </div>
              <span className="card__network">
                Network
                <br />
                mark
              </span>
            </footer>
            {/* No card is issued and none of these figures is anyone's (#140).
                Cards is still a "not built yet" page; this panel is the
                design's artwork and now says so. */}
            <p className="send__sample">Card is not issued yet. This is the design&rsquo;s artwork.</p>
          </section>
        </aside>
      </div>
    </>
  )
}

/**
 * The two letters in the disc.
 *
 * A currency code truncated to two, or a counterparty's initials. Decorative
 * and `aria-hidden`, so it never has to carry meaning on its own.
 */
function initialsOf(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
  return text.slice(0, 2).toUpperCase()
}

/**
 * One account and what is in it.
 *
 * The account row is drawn whether or not its balance arrived, because the
 * account is a fact `GET /v1/accounts` already gave us and the balance is a
 * separate read that can fail on its own (#140). A row that vanishes because a
 * figure did not land tells the customer they do not hold something they do.
 */
function HoldingRow({
  account,
  balance,
  now,
}: {
  account: Account
  balance: BalanceState
  now: Date
}) {
  return (
    <tr>
      <td className="holdings__discCell">
        {/* The symbol, as Balances draws it. Initials of a currency code —
            "EU" for EUR — is a country. */}
        <span className="disc" aria-hidden="true">
          {CURRENCY_SYMBOL[account.currency] ?? account.currency}
        </span>
      </td>
      <td>
        {/* The NAME, not the code: the disc beside it is already the symbol,
            and "EUR / EUR" said the same thing twice. Falls back to the code,
            which is not wrong — only shorter. */}
        <span className="holdings__name">
          {CURRENCY_NAME[account.currency] ?? account.currency}
        </span>
        <span className="holdings__note">
          {/* The backend's sentence, rendered verbatim — one wording across web,
              iOS and Android rather than three apps disagreeing about what
              "restricted" means to the person it happened to. */}
          {account.state === 'active' ? account.notice.headline : account.notice.detail}
        </span>
      </td>
      <td className="holdings__right">
        {balance.status === 'ready' ? (
          <>
            {/*
              ONE FIGURE, unless the gap is one a bank would show (#174).

              This rendered `balance` over `available` unconditionally, which on
              a live account read `100,000.00 / 93,602.86 available` — a pair a
              customer reads as "a hold that will clear" when it is a
              reconciliation break that never clears and widens with every
              credit. The institution's figure is the balance when the two
              disagree; ours is an internal number that has drifted from the
              account, and Balances.tsx carries the argument in full.

              `available` is never recomputed: it is `min(balance − pending_out,
              the institution's figure)` and the backend computes it with the
              same code the money path uses.
            */}
            <span className="holdings__amount tnum">
              {formatMoney(
                balance.balance.cappedBy === 'provider'
                  ? balance.balance.available
                  : balance.balance.balance,
              )}
            </span>
            {balance.balance.pendingOut.amountMinor !== 0n ||
            isStale(balance.balance.providerAsOf, now) ? (
              <span className="holdings__note tnum">
                {formatMoney(balance.balance.available, { withCurrency: true })} available
                {balance.balance.pendingOut.amountMinor !== 0n
                  ? ` · ${formatMoney(balance.balance.pendingOut)} on its way out`
                  : ''}
                {isStale(balance.balance.providerAsOf, now) ? ' · not up to the minute' : ''}
              </span>
            ) : null}
          </>
        ) : (
          <span className="holdings__note">
            {/*
              NEVER a zero. `503 balance_unavailable` covers a stale snapshot, a
              missing wallet and a read failure as one answer deliberately, so
              there is nothing to distinguish and nothing to assume.
            */}
            {balance.status === 'loading' ? '…' : 'No figure just now'}
          </span>
        )}
      </td>
    </tr>
  )
}

/** One row of history. */
function ActivityRow({ tx, now }: { tx: Transaction; now: Date }) {
  // Absent, never blank — an empty string reads as a counterparty called
  // nothing, which is why the contract omits the field instead.
  const who = tx.counterparty ?? 'Not named'
  // `state` is what was RECORDED and is never inferred from the date. A settled
  // item and a pending one can share a value date.
  const settledNote = tx.state === 'settled' ? undefined : tx.state
  return (
    <tr>
      <td className="holdings__discCell">
        {/* Nothing rather than initials of "Not named", which reads as a
            counterparty whose name begins NN. */}
        <span className="disc disc--plain" aria-hidden="true">
          {tx.counterparty === undefined ? '' : initialsOf(tx.counterparty)}
        </span>
      </td>
      <td>
        <span className="holdings__name">{who}</span>
        <span className="holdings__note">
          {/*
            `message.display` gates the text, and reading `text` without it is
            the gap FR-22.3 closes: the rail cannot tell a platform's payout
            reference from a stranger's 140 characters, so raw text is withheld
            by default and `notice` says so instead.
          */}
          {tx.message?.text ?? tx.message?.notice ?? settledNote ?? ''}
        </span>
      </td>
      <td className="holdings__col-date">
        <span className="holdings__note">
          <time dateTime={tx.valueDate}>{formatActivityDate(tx.valueDate, now)}</time>
        </span>
      </td>
      <td className="holdings__right">
        <span className="holdings__amount tnum" data-direction={tx.direction}>
          {/*
            The AUTHORISED amount, with the direction's sign. `settled` is
            absent until the institution has spoken and rendering zero for it
            would claim it settled nothing when it has said nothing at all.
          */}
          {formatMoney(
            tx.direction === 'out'
              ? { ...tx.amount, amountMinor: -absOf(tx.amount.amountMinor) }
              : { ...tx.amount, amountMinor: absOf(tx.amount.amountMinor) },
            { signed: true, withCurrency: true },
          )}
        </span>
      </td>
    </tr>
  )
}

/**
 * `direction` carries the sign, so the magnitude is taken from `amount`.
 *
 * The contract does not say whether an outbound amount arrives negative, and a
 * client that assumed either way would render `− −40.00` or `+ 40.00` for a
 * payment out the first time the backend disagreed with it. Reading the
 * direction and the magnitude separately is right under both.
 *
 * This is the one place outside `money.ts` that touches an amount, and it does
 * arithmetic on none: `-x` on a bigint changes a sign and cannot lose a digit.
 */
function absOf(n: bigint): bigint {
  return n < 0n ? -n : n
}
